import { useMemo } from 'react';
import { Column, Row, usePagination, useSortBy, useTable } from 'react-table';

import ReactTable from '../../component/ReactTable';
import { COLOUR_BACKUP_FAILED } from '../../component/Constants';
import { formatBytes, getDefaultPageSize } from '../../helpers/utils';
import BackupHistoryItem from '../../types/backup/BackupHistoryItem';
import BackupSummary from '../../types/backup/BackupSummary';
import { chainBreakLabel, isPossibleGap, newChainStarts } from './backupChainUtils';
import {
    BACKUP_KIND_COLOURS,
    backupKind,
    backupTypeLabel,
    formatBackupDuration,
    formatBackupTime,
} from './backupUtils';

interface Props {
    items: BackupHistoryItem[];
    loading: boolean;
    // Show which replica took each backup of an availability group database.
    showReplica: boolean;
    // The database's summary, which says whether a break may only be backups
    // taken on a replica that is not monitored.
    row: BackupSummary;
}

function yesNo(value?: boolean) {
    return value === undefined || value === null ? '-' : value ? 'Yes' : 'No';
}

// Renders the Yes/No accessor value as a tick or cross, keeping the text for screen readers.
function YesNoIcon({ value }: { value: string }) {
    if (value === '-') {
        return <>-</>;
    }
    return <i className={`fas fa-fw ${value === 'Yes' ? 'fa-check' : 'fa-times'}`} role="img" aria-label={value}/>;
}

function BackupHistoryTable(props: Props) {
    const chainStarts = useMemo(() => newChainStarts(props.items), [props.items]);

    const columns = useMemo<Column<BackupHistoryItem>[]>(() => {
        // className is read by ReactTable but is not part of the Column type.
        const list = [
            {
                Header: 'Type',
                accessor: 'backuptype',
                Cell: ({ row }: { row: Row<BackupHistoryItem> }) => {
                    const item = row.original;
                    const failed = item.status === 'failed';
                    const possibleGap = isPossibleGap(item, props.row);
                    return <span data-tooltip-id="global-tooltip"
                                 data-tooltip-content={failed ? item.message : undefined}>
                        <span className="backups__swatch me-1" style={{
                            backgroundColor: failed ? COLOUR_BACKUP_FAILED
                                : BACKUP_KIND_COLOURS[backupKind(item.backuptype)],
                        }}/>
                        {backupTypeLabel(item.backuptype)}
                        {failed && <span className="red-icon ms-1">
                            <i className="fas fa-circle-xmark me-1" role="img" aria-label={item.message}/>Failed
                        </span>}
                        {item.chainbreak && <span
                            className={`badge ${possibleGap ? 'bg-secondary' : 'bg-danger'} ms-1`}
                            data-tooltip-id="global-tooltip"
                            data-tooltip-content={`Log backups are missing after ${formatBackupTime(
                                item.chainbreakafter)}: this one does not start where the previous one ended${
                                possibleGap
                                    ? '. They may have been taken on an availability group replica that is not monitored'
                                    : ''}`}>
                            {chainBreakLabel(possibleGap)}
                        </span>}
                        {chainStarts.has(item) && <span className="badge bg-success ms-1"
                            data-tooltip-id="global-tooltip"
                            data-tooltip-content="The log backups after the break restore from this backup">
                            Starts new chain
                        </span>}
                        {item.adhocfull && <span className="badge bg-warning text-dark ms-1"
                            data-tooltip-id="global-tooltip"
                            data-tooltip-content={'Unlike the full backups either side of it, so probably ad hoc. ' +
                                'It reset the differential base; use COPY_ONLY for one-off full backups'}>
                            Ad hoc?
                        </span>}
                    </span>;
                },
            },
            {
                Header: 'Start date',
                id: 'timeslice',
                accessor: item => Date.parse(item.timeslice),
                sortType: 'basic',
                Cell: ({ row }: { row: Row<BackupHistoryItem> }) => formatBackupTime(row.original.timeslice),
            },
            {
                Header: 'Duration',
                id: 'duration',
                accessor: item => item.duration ?? -1,
                sortType: 'basic',
                className: 'text-end',
                Cell: ({ row }: { row: Row<BackupHistoryItem> }) => formatBackupDuration(row.original.duration),
            },
            {
                Header: 'Size',
                id: 'size',
                accessor: item => item.size ?? -1,
                sortType: 'basic',
                className: 'text-end',
                Cell: ({ row }: { row: Row<BackupHistoryItem> }) => formatBytes(row.original.size),
            },
            {
                Header: 'Compressed size',
                id: 'compressedsize',
                accessor: item => item.compressedsize ?? -1,
                sortType: 'basic',
                className: 'text-end',
                Cell: ({ row }: { row: Row<BackupHistoryItem> }) => formatBytes(row.original.compressedsize),
            },
            {
                Header: 'Copy only',
                id: 'iscopyonly',
                accessor: item => yesNo(item.iscopyonly),
                Cell: YesNoIcon,
            },
            {
                Header: 'Compressed',
                id: 'iscompressed',
                accessor: item => yesNo(item.iscompressed),
                Cell: YesNoIcon,
            },
            {
                Header: 'Encrypted',
                id: 'isencrypted',
                accessor: item => yesNo(item.isencrypted),
                Cell: YesNoIcon,
            },
            {
                Header: 'Location',
                id: 'location',
                accessor: item => item.location || '',
                Cell: ({ value }: { value: string }) =>
                    <span className="backups__location" title={value}>{value || '-'}</span>,
            },
        ] as Column<BackupHistoryItem>[];

        if (props.showReplica) {
            list.push({
                Header: 'Replica',
                id: 'replica',
                accessor: item => item.servername || item.name,
            });
        }

        return list;
    }, [props.showReplica, props.row, chainStarts]);

    const tableInstance = useTable<BackupHistoryItem>({
            columns,
            data: props.items,
            initialState: { pageSize: getDefaultPageSize(), sortBy: [{ id: 'timeslice', desc: true }] } as any,
            autoResetPage: false,
        } as any,
        useSortBy, usePagination);

    const {
        getTableProps, getTableBodyProps, headerGroups, prepareRow, page, canPreviousPage, canNextPage,
        pageOptions, pageCount, gotoPage, nextPage, previousPage, setPageSize, state: { pageIndex, pageSize },
    } = tableInstance as any;

    return <ReactTable<BackupHistoryItem>
        getTableProps={getTableProps}
        getTableBodyProps={getTableBodyProps}
        headerGroups={headerGroups}
        prepareRow={prepareRow}
        page={page}
        canPreviousPage={canPreviousPage}
        canNextPage={canNextPage}
        pageOptions={pageOptions}
        pageCount={pageCount}
        gotoPage={gotoPage}
        nextPage={nextPage}
        previousPage={previousPage}
        setPageSize={setPageSize}
        pageIndex={pageIndex}
        pageSize={pageSize}
        dataLength={props.items.length}
        loading={props.loading}
        getRowProps={(row) => ({
            className: row.original.status === 'failed' ? 'backups__failed' : '',
        })}
    />;
}

export default BackupHistoryTable;
